<template>
  <div class="highlight-tile-container" :class="{ 'is-expanded': isExpandedLocal, 'is-overflowing': isOverflowing }">
    <HighlightIcon v-if="isEligibleCategory" :attribute-name="name" :is-mattress="isMattress" :aria-label="facetDisplayName" />
    <div class="attribute-name small">{{ facetDisplayName }}</div>
    <div class="values-wrapper">
      <p :id="valuesId" ref="valuesRef" class="values large">
        {{ value
        }}<button
          v-if="isOverflowing"
          type="button"
          class="caret-button"
          :aria-expanded="isExpandedLocal"
          :aria-label="isExpandedLocal ? `Collapse ${facetDisplayName}` : `Expand ${facetDisplayName}`"
          :aria-controls="valuesId"
          @click="handleClick"
        >
          <span :class="['chevron-icon', isExpandedLocal ? 'chevron-small-up-dark-medium' : 'chevron-small-down-dark-medium']"></span>
        </button>
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, watch, computed, toValue } from 'vue';
import HighlightIcon from './HighlightIcon.vue';
import { useProductDetails } from '../composables/useProductDetails';

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  facetDisplayName: {
    type: String,
    required: true,
  },
  value: {
    type: String,
    required: true,
  },
  isRowExpanded: {
    type: Boolean,
    default: false,
  },
});

const valuesId = computed(() => `highlight-values-${props.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`);

const { typeName } = useProductDetails();

const resolvedTypeName = computed(() => toValue(typeName) || '');

const isEligibleCategory = computed(() => {
  const eligibleTypes = ['MATTRESS', 'SKIN_CARE', 'SKINCARE', 'MAKEUP', 'FACE', 'EYE', 'LIPS', 'FRAGRANCE'];
  return eligibleTypes.includes(resolvedTypeName.value);
});

const isMattress = computed(() => resolvedTypeName.value === 'MATTRESS');

const emit = defineEmits(['expand-request', 'collapse-request']);

const valuesRef = ref(null);
const isOverflowing = ref(false);
const isExpandedLocal = ref(props.isRowExpanded);

const handleClick = () => {
  if (isExpandedLocal.value) {
    emit('collapse-request');
  } else {
    emit('expand-request');
  }
  isExpandedLocal.value = !isExpandedLocal.value;
};

watch(
  () => props.isRowExpanded,
  (isNowExpanded) => {
    isExpandedLocal.value = isNowExpanded;
  },
);

const checkOverflow = () => {
  const el = valuesRef.value;
  if (!el) return;

  const prevMaxHeight = el.style.maxHeight;
  const prevOverflow = el.style.overflow;
  el.style.maxHeight = 'var(--collapsed-max-height)';
  el.style.overflow = 'hidden';

  isOverflowing.value = el.scrollHeight > el.clientHeight + 1; // +1: subpixel rounding

  el.style.maxHeight = prevMaxHeight;
  el.style.overflow = prevOverflow;
};

onMounted(() => {
  checkOverflow();
});

watch(
  () => props.value,
  () => {
    nextTick(checkOverflow);
  },
);
</script>

<style lang="scss" scoped>
@import '~settingSCSS';
@import '~@csgIcons/new/chevron-small-down-dark-medium.scss';
@import '~@csgIcons/new/chevron-small-up-dark-medium.scss';

.highlight-tile-container {
  display: flex;
  flex-direction: column;
  align-content: flex-start;
  position: relative;
  --tile-background: #{$body-background};
}

.attribute-name {
  color: $gray-1-color;
}

.values-wrapper {
  position: relative;
  margin-top: rem-calc(4);
}

.values {
  --collapsed-max-height: calc(2 * 1.4em); /* single source of truth, also read by JS */
  line-height: 1.4;
  word-break: normal;
  max-height: var(--collapsed-max-height);
  overflow: hidden;
}

.highlight-tile-container.is-expanded .values {
  max-height: none;
  overflow: visible;
}

.caret-button {
  background: none;
  border: none;
  padding: 0;
  margin-left: rem-calc(2);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  font-size: inherit; /* buttons don't inherit font — needed for em sizing below */
  line-height: 1;
  vertical-align: middle;
}

.highlight-tile-container:not(.is-expanded) .caret-button {
  position: absolute;
  right: 0;
  bottom: 0;
  margin-left: 0;
  height: 1.4em; /* exactly one line box */
  padding-left: rem-calc(40); /* width of the fade zone over the text */
  background: linear-gradient(to right, transparent, var(--tile-background) 60%);
}

.chevron-icon {
  display: inline-block;
}
</style>
